/**
 * Kundenliste mit Ampeln (Redesign 03.09.2026, Richtung „Kunden-Cockpit").
 *
 * Eine Zeile je Kunde: Pakete · Google-Ads-Ampel · Kontaktanfragen 30 T. ·
 * Bericht · zuletzt. Klick öffnet den Arbeitsbereich /kunden/:id.
 * Anlegen/Einladen/Bearbeiten bleibt unter /kunden/verwalten (alte Seite).
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Search, Settings2 } from 'lucide-react';
import { getKundenStatus } from '@/lib/analytics-api';
import Ampel from '@/components/Ampel';

function relativ(iso: string): string {
  const t = Math.round((Date.now() - new Date(iso).getTime()) / 86400000);
  if (t <= 0) return 'heute';
  if (t === 1) return 'gestern';
  return `vor ${t} T.`;
}

export default function KundenPage() {
  const navigate = useNavigate();
  const [suche, setSuche] = useState('');
  const [inaktive, setInaktive] = useState(false);
  const { data, isLoading, error } = useQuery({ queryKey: ['kunden-status'], queryFn: getKundenStatus, staleTime: 60_000 });

  const liste = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return (data?.kunden || []).filter((k) => (inaktive || k.active) && (!q || k.name.toLowerCase().includes(q)));
  }, [data, suche, inaktive]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold text-gray-900">Kunden</h1>
        <span className="text-sm text-gray-400">{liste.length}</span>
        <div className="relative ml-auto">
          <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Kunde suchen…" className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg w-56 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300" />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
          <input type="checkbox" checked={inaktive} onChange={(e) => setInaktive(e.target.checked)} className="rounded" /> inaktive zeigen
        </label>
        <Link to="/kunden/verwalten" className="inline-flex items-center gap-1.5 text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50">
          <Settings2 className="w-4 h-4" /> Verwalten
        </Link>
      </div>

      {isLoading && <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>}
      {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}

      {data && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                <th className="px-4 py-2.5 font-semibold">Kunde</th>
                <th className="px-3 py-2.5 font-semibold">Pakete</th>
                <th className="px-3 py-2.5 font-semibold">Google Ads</th>
                <th className="px-3 py-2.5 font-semibold text-right">Kontaktanfragen 30 T.</th>
                <th className="px-3 py-2.5 font-semibold">Bericht</th>
                <th className="px-3 py-2.5 font-semibold">Zuletzt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {liste.map((k) => (
                <tr key={k.id} onClick={() => navigate(`/kunden/${k.id}`)} className="hover:bg-gray-50 cursor-pointer">
                  <td className="px-4 py-2.5">
                    <span className={`font-medium ${k.active ? 'text-gray-900' : 'text-gray-400'}`}>{k.name}</span>
                    {!k.active && <span className="ml-2 text-[11px] text-gray-400">inaktiv</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="flex flex-wrap gap-1">
                      {k.pakete.map((p) => <span key={p} className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{p}</span>)}
                      {k.pakete.length === 0 && <span className="text-gray-300">—</span>}
                    </span>
                  </td>
                  <td className="px-3 py-2.5"><Ampel status={k.ads.status} text={k.ads.text} /></td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    <span className="font-medium text-gray-900">{k.anfragen_30d.formular + k.anfragen_30d.klicks}</span>
                    {(k.anfragen_30d.formular + k.anfragen_30d.klicks) > 0 && <span className="text-gray-400 text-xs"> · {k.anfragen_30d.formular} Formular / {k.anfragen_30d.klicks} Klick</span>}
                  </td>
                  <td className="px-3 py-2.5"><Ampel status={k.bericht.status} text={k.bericht.text} /></td>
                  <td className="px-3 py-2.5 text-gray-500">{k.zuletzt ? `${k.zuletzt.text} · ${relativ(k.zuletzt.zeit)}` : '—'}</td>
                </tr>
              ))}
              {liste.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Kein Kunde gefunden.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
